// Presigned image cache
// ----------------------
// Holds presigned S3 images as in-memory blobs so what is on screen stops
// depending on the life of the signature that delivered it.
//
// Used by every image the backend presigns — vulnerability screenshots
// (Vulnerabilities.render, additionalRenders[].url) and user avatars
// (User.avatar). All of them are resolved by the same presign field resolver
// and share the same expiry, so all of them fail the same way.
//
// WHY A CACHE AT ALL. These are not stored URLs: AppSync presigns the underlying
// S3 key at query time, and the signature is valid for
// PRESIGNED_URL_EXPIRY_SECONDS -- 900s, in the backend's models/definitions.py.
// The clock starts when the record is read, so a page left open for a quarter of
// an hour has dead image URLs while the Cognito session is still refreshing
// happily in the background. Fetch the bytes once and the picture survives
// however long the tab stays open.
//
// WHY NOT THE BROWSER'S OWN HTTP CACHE. It keys on the full URL, and every
// re-query mints a fresh X-Amz-Signature and X-Amz-Date. The same screenshot is
// therefore a brand new cache entry every time and never a hit. Keying on the
// object key -- the URL path, which is stable across re-signings -- is the whole
// point of this module.
//
// WHY MEMORY RATHER THAN IndexedDB OR THE CACHE API. On reload the app re-queries
// and re-signs anyway, so persisting to disk saves bandwidth and nothing else.
// Set against that, it would write customer vulnerability evidence -- screenshots
// of working exploits -- onto the user's disk, outliving the session that was
// authorised to see them and undercutting the very expiry the short TTL exists
// to enforce. Object URLs die with the tab, which is the correct lifetime here.
//
// Requires GET in the client bucket's CORS rule (see the infra repo's
// new-client-nimis-ui-deployment-script.json). Callers MUST cope with this
// rejecting: where CORS has not yet been updated, the plain <img> path still
// works, because image elements are not subject to CORS.

// Generous enough that eviction is rare in practice -- a finding shows a handful
// of screenshots -- which matters because eviction revokes an object URL that a
// mounted <img> could still be pointing at. Sized to bound a long browsing
// session rather than to be hit routinely.
const MAX_CACHED_BYTES = 32 * 1024 * 1024;

type CachedEvidence = {
  objectUrl: string;
  sizeBytes: number;
};

// Insertion-ordered, and entries are re-inserted when read, so the first entry
// is always the least recently used.
const cacheByObjectKey = new Map<string, CachedEvidence>();
// Collapses concurrent requests for the same screenshot into one fetch — the
// primary render and a thumbnail of it would otherwise both go to the network.
const inFlightByObjectKey = new Map<string, Promise<string>>();
let totalCachedBytes = 0;

// Whether this URL is one that expires and therefore benefits from caching.
//
// Not every avatar URL is presigned: the user model substitutes the bundled
// placeholder (a webpack /static/media asset) for users who never uploaded one,
// and an externally hosted avatar is passed through untouched. Those do not
// expire, cost a pointless request to copy into a blob, and — for the bundled
// asset — would be cached once per user for no reason at all. Sniffing for the
// AWS signature targets exactly the URLs with a deadline on them.
export function isExpiringPresignedUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.indexOf("X-Amz-Signature=") !== -1;
}

// The signature-free identity of the object. Two presigned URLs for the same
// screenshot differ only in their query string, so the path is what makes a
// re-signed URL recognisable as something already held.
function toObjectKey(presignedUrl: string): string {
  try {
    return new URL(presignedUrl).pathname;
  } catch {
    // Not an absolute URL — fall back to the whole string. Worst case is a
    // cache miss, never a wrong hit.
    return presignedUrl;
  }
}

function evictUntilWithinBudget(protectedObjectKey: string): void {
  // Snapshot the keys first: the map is mutated as entries are dropped, and the
  // project targets ES5, where a Map is not directly iterable.
  const leastRecentlyUsedFirst = Array.from(cacheByObjectKey.keys());

  for (let index = 0; index < leastRecentlyUsedFirst.length; index += 1) {
    if (totalCachedBytes <= MAX_CACHED_BYTES) return;

    const objectKey = leastRecentlyUsedFirst[index];
    // Never evict the entry that was just stored: its object URL is about to be
    // handed to a caller, and revoking it would return a dead reference.
    if (objectKey === protectedObjectKey) continue;

    const cached = cacheByObjectKey.get(objectKey);
    if (!cached) continue;

    URL.revokeObjectURL(cached.objectUrl);
    totalCachedBytes -= cached.sizeBytes;
    cacheByObjectKey.delete(objectKey);
  }
}

// Resolves to an object URL for the screenshot, fetching it once and reusing it
// thereafter. Rejects when the bytes cannot be read (CORS not yet deployed, the
// signature already expired, the object gone) — the caller decides what to show.
export async function loadPresignedImageObjectUrl(presignedUrl: string): Promise<string> {
  const objectKey = toObjectKey(presignedUrl);

  const cached = cacheByObjectKey.get(objectKey);
  if (cached) {
    // Re-insert to mark it most recently used.
    cacheByObjectKey.delete(objectKey);
    cacheByObjectKey.set(objectKey, cached);
    return cached.objectUrl;
  }

  const inFlight = inFlightByObjectKey.get(objectKey);
  if (inFlight) return inFlight;

  const evidenceRequest = (async (): Promise<string> => {
    const response = await fetch(presignedUrl);
    if (!response.ok) {
      // 403 here is the expired signature; anything else is a genuine failure.
      throw new Error(`Evidence fetch failed with status ${response.status}`);
    }
    const evidenceBlob = await response.blob();
    const objectUrl = URL.createObjectURL(evidenceBlob);
    cacheByObjectKey.set(objectKey, { objectUrl, sizeBytes: evidenceBlob.size });
    totalCachedBytes += evidenceBlob.size;
    evictUntilWithinBudget(objectKey);
    return objectUrl;
  })();

  inFlightByObjectKey.set(objectKey, evidenceRequest);
  try {
    return await evidenceRequest;
  } finally {
    inFlightByObjectKey.delete(objectKey);
  }
}

// Drops every held screenshot and releases its object URL. Called on sign-out:
// logging out is a client-side route change, not a reload, so without this the
// previous user's evidence would stay in memory for whoever logs in next on the
// same tab.
export function clearPresignedImageCache(): void {
  cacheByObjectKey.forEach((cached) => {
    URL.revokeObjectURL(cached.objectUrl);
  });
  cacheByObjectKey.clear();
  inFlightByObjectKey.clear();
  totalCachedBytes = 0;
}
